import React, { FC, useCallback, useMemo, useState } from 'react';
import { purchaseRequestLineItemsRestCrudThunksForSupplier } from '../../thunks/purchase_request_line_items_thunks';
import { Button, Modal } from 'antd';
import { connect } from 'react-redux';
import { PR_LINE_ITEM_COLUMNS } from '../purchase_requests_index_page/PRLineItemsComponent';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { EmptyState } from '../empty_state/EmptyState';
import { getPOLineItemsFromSelectedRecords } from '../purchase_requests_index_page/pr_to_po_utils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const AddLineItemsToPOModal: FC<any> = ({
	isEquipmentLine,
	stockLocationId,
	locationId,
	partEquipmentVendorId,
	existingPartLineItems,
	existingEquipmentLineItems,
	onCancel,
	onAddLineItemsSuccess,
}): React.ReactElement => {
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedRecords, setSelectedRecords] = useState([]);

	const title = useMemo(() => (isEquipmentLine ? 'Add Equipment' : 'Add Parts'), [isEquipmentLine]);

	const onSelectChange = useCallback((newSelectedRowKeys: React.Key[], selectedRows) => {
		setSelectedRowKeys(newSelectedRowKeys);
		setSelectedRecords(selectedRows);
	}, []);

	const allPRLineItemIds = useMemo(
		() =>
			[...(existingPartLineItems || []), ...(existingEquipmentLineItems || [])].reduce(
				(acc, lineItem) => [...acc, ...(lineItem.prLineItemIds || [])],
				[]
			),
		[existingEquipmentLineItems, existingPartLineItems]
	);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			preserveSelectedRowKeys: true,
			onChange: onSelectChange,
			getCheckboxProps: (record) => ({
				disabled: allPRLineItemIds.includes(record.id),
			}),
		}),
		[allPRLineItemIds, onSelectChange, selectedRowKeys]
	);

	const onAddLineItems = useCallback(() => {
		const pli = existingPartLineItems.reduce(
			(acc, item) => ({
				...acc,
				[item.partId]: {
					...item,
					unitCost: nullSafeGet('partCost', item)
						? item.partCost / nullSafeGetOrElse('partQuantity', item, 1)
						: undefined,
				},
			}),
			{}
		);

		const eli = existingEquipmentLineItems.reduce(
			(acc, item) => ({
				...acc,
				[item.equipmentId]: {
					...item,
					unitCost: nullSafeGet('equipmentCost', item)
						? item.equipmentCost / nullSafeGetOrElse('equipmentQuantity', item, 1)
						: undefined,
				},
			}),
			{}
		);

		const { partLineItems, equipmentLineItems } = getPOLineItemsFromSelectedRecords(
			selectedRecords,
			pli,
			eli
		);

		onAddLineItemsSuccess({
			partLineItems,
			equipmentLineItems,
		});
	}, [existingEquipmentLineItems, existingPartLineItems, onAddLineItemsSuccess, selectedRecords]);

	return (
		<Modal
			visible={true}
			width={'80%'}
			title={title}
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					className="ml-2"
					onClick={onAddLineItems}
					disabled={!selectedRecords.length}
				>
					{title}
				</Button>,
			]}
		>
			<PaginatedReduxTable
				mode="list"
				rowSelection={rowSelection}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No valid purchase requests"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No valid purchase requests found'}
						body={null}
					/>
				}
				collectionName="purchase_request_line_items"
				targetCollectionName="purchaseRequestLineItemsIndex"
				columns={PR_LINE_ITEM_COLUMNS}
				showHeader
				keyAccessor={(el) => el.id}
				initialFilters={{
					...(stockLocationId && { stockLocationId }),
					...(locationId && { locationId }),
					partEquipmentVendorIdOrNull: partEquipmentVendorId,
					isEquipmentLine: !!isEquipmentLine,
					status: 'approved',
				}}
				fetchData={purchaseRequestLineItemsRestCrudThunksForSupplier.read}
			/>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	purchaseRequestLineItems: state.purchase_request_line_items,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	fetchPRLineItems: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			purchaseRequestLineItemsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddLineItemsToPOModal);
